import { LightningElement, api, wire, track } from "lwc";
import classNames from "classnames";

import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { BooleanAttr, attrToBool } from "common/helpers";

import * as locales from "./locales";

import input from "./input.html";
import inputSearch from "./inputSearch.html";
import inputCheckbox from "./inputCheckbox.html";
import inputRadio from "./inputRadio.html";
import inputToggle from "./inputToggle.html";
import inputFile from "./inputFile.html";
import { FormControlType } from "../formControl/types";
import { Config } from "tds/sanitizedHtml";

export type InputType =
  | "text"
  | "search"
  | "checkbox"
  | "radio"
  | "toggle"
  | "file";

export type ToggleLabels = {
  toggleEnabled: string;
  toggleDisabled: string;
};

export const defaultHtmlConfig: Config = {
  ALLOWED_TAGS: ["a", "strong", "em"],
  ADD_ATTR: ["target"],
};

export function isToggleable(type?: InputType | FormControlType) {
  return !!(type && ["checkbox", "radio", "toggle"].includes(type));
}

export function isSearch(type?: InputType | FormControlType) {
  return type === "search";
}

export default class extends LightningElement {
  @api accept: string | null = null;
  @api ariaInvalid: string | null = null;
  @api ariaLabel: string | null = null;
  @api autocomplete: string = "off";
  @api busy: BooleanAttr = false;
  @api checked: BooleanAttr = false;
  @api disabled: BooleanAttr = false;
  @api errorMessage: string | null = null;
  @api label: string | null = null;
  @api tooltip: string | null = null;
  @api fileSelectorLabel: string | null = null;
  @api fileSelectedLabel: string | null = null;
  @api name: string | null = null;
  @api placeholder: string | null = null;
  @api required: string | null = null;
  @api type: InputType = "text";
  @api value: string = "";
  @api readOnly: BooleanAttr = false;
  @api minSearchChars: number | null = null;
  @api subLabel: string | null = null;
  @api dragDropLabel: string | null = null;
  @api maxLength: string | null = null;
  @api fileLoading: BooleanAttr = false;
  @api fileImgUrl: string | ArrayBuffer | null = "";
  @api boldLabel: BooleanAttr = false;
  @api ariaDescribedby: string | null = null;
  @api htmlConfig: Config = defaultHtmlConfig;

  @track private file: File | null = null;

  private loadingPreview = false;

  @wire(LabelsAdapter, { locales })
  private labels: LabelsAdapterValue = null!;

  @api toggleLabels: ToggleLabels = {
    toggleEnabled: "",
    toggleDisabled: "",
  }!;

  @api
  focus() {
    this.template.querySelector("input")?.focus();
  }

  render() {
    switch (this.type) {
      case "search":
        return inputSearch;
      case "checkbox":
        return inputCheckbox;
      case "radio":
        return inputRadio;
      case "toggle":
        return inputToggle;
      case "file":
        return inputFile;
      default:
        return input;
    }
  }

  private get _value() {
    return typeof this.value === "string" ? this.value : null;
  }

  private get className() {
    return classNames("input", {
      "input--disabled": this.isDisabled,
      "input--errored": this.hasError,
    });
  }

  private get labelClasses() {
    return classNames("label", {
      "label--required": this.isRequired,
      "input-file__field-label": this.type === "file",
      "bold-label": attrToBool(this.boldLabel),
    });
  }

  private get showClearButton() {
    return this._value && !this.isBusy;
  }

  private get isDisabled() {
    return attrToBool(this.disabled);
  }

  private get isRequired() {
    return attrToBool(this.required);
  }

  private get isInvalid() {
    if (this.ariaInvalid || attrToBool(this.ariaInvalid)) {
      return attrToBool(this.ariaInvalid);
    }

    if ((this.isRequired && !this._value) || this.hasError) {
      return true;
    }

    if (this.minSearchChars && this.value.length < this.minSearchChars) {
      return true;
    }

    return false;
  }

  private get isTypeText(): boolean {
    return this.type === "text";
  }

  private get isBusy() {
    return attrToBool(this.busy);
  }

  private get isChecked() {
    return attrToBool(this.checked);
  }

  private get loading() {
    return this.loadingPreview || this.fileLoading;
  }

  // `file` is set when the user explicitly chooses a file.
  // `fileImgUrl` is set after the user chooses a file OR set when mounted in an edit form
  private get hasFile() {
    return this.file || this.fileImgUrl;
  }

  private handleChange(e: { target: HTMLInputElement }) {
    if (isToggleable(this.type)) {
      return this.dispatchEvent(
        new CustomEvent("change", {
          detail: e.target.checked,
        })
      );
    }

    if (isSearch(this.type)) {
      this.value = e.target.value;
      if (this.minSearchChars && e.target.value.length >= this.minSearchChars) {
        this.errorMessage = "";
      }
    }

    if (this.type === "file") {
      const file = e.target.files![0];
      e.target.value = ""; // Allows same file selection after delete
      this.digestFile(file);
    } else if (!isSearch(this.type) || !this.minSearchChars) {
      this.dispatchEvent(new CustomEvent("change", { detail: e.target.value }));
    }
  }

  private handleClearValue(e: Event) {
    e.preventDefault();
    this.value = "";
    this.errorMessage = "";
    this.dispatchEvent(new CustomEvent("change", { detail: "" }));
  }

  private get hasError() {
    if (this.minSearchChars && this.value.length >= this.minSearchChars) {
      return false;
    }
    return !!this.errorMessage;
  }

  private get minSearchCharError() {
    return this.labels.error;
  }

  private handleKeyUp(e: Event) {
    const input = e.target as HTMLInputElement;
    if (this.ariaInvalid || attrToBool(this.ariaInvalid)) {
      return attrToBool(this.ariaInvalid);
    }

    if (this.isRequired) {
      input.value
        ? input.setAttribute("aria-invalid", "false")
        : input.setAttribute("aria-invalid", "true");
    }

    if (this.minSearchChars) {
      input.value.length >= this.minSearchChars
        ? input.setAttribute("aria-invalid", "false")
        : input.setAttribute("aria-invalid", "true");
    }
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      if (isSearch(this.type) && this.minSearchChars) {
        this.value.length >= this.minSearchChars
          ? this.dispatchEvent(
              new CustomEvent("change", { detail: this.value })
            )
          : (this.errorMessage = this.minSearchCharError);
      } else {
        this.dispatchEvent(new CustomEvent("change", { detail: this.value }));
      }
    }
  };

  private get isReadOnly() {
    return attrToBool(this.readOnly);
  }

  private handleDragOver(e: Event) {
    e.preventDefault();
  }

  private handleDrop(e: DragEvent) {
    e.preventDefault();
    if (this.type === "file") {
      const file = e.dataTransfer!.files[0];
      this.digestFile(file);
    }
  }

  private digestFile(file: File) {
    if (file) {
      this.file = file;
      if (file.type.includes("image")) {
        this.displayFilePreview(file);
      }
      this.dispatchEvent(new CustomEvent("change", { detail: file }));
    }
  }

  private displayFilePreview(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      this.fileImgUrl = reader.result;
      this.loadingPreview = false;
    };
    this.loadingPreview = true;
    reader.readAsDataURL(file);
  }

  private handleFileSelectorClick(e: Event) {
    e.preventDefault();
    this.template.querySelector("input")?.click();
  }

  private deleteFile(e: Event) {
    e.preventDefault();
    this.file = null;
    this.fileImgUrl = null;
    this.dispatchEvent(new CustomEvent("change", { detail: null }));
  }
}
